import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationSettings } from '@/hooks/useNotificationSettings';
import { registerPolling, POLL_FALLBACK, POLL_FALLBACK_SLOW } from '@/hooks/usePolling';
import { playNotificationChime } from '@/lib/notificationSound';

/** Badging API（OSのアプリバッジ）を持つ Navigator の拡張。 */
interface BadgeNavigator extends Navigator {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

/** task_notifications のうち、ITサポートの空き通知を表す種別。 */
const AVAILABILITY_TYPE = 'it_support_available';

/**
 * 「自分の空き待ちに IT担当者の空きが出た」瞬間をアプリ全体で検知し、
 * **アプリを開いている間はアプリ内の音＋バッジ**で知らせるフック。
 *
 * - 自分の空き待ち（`it_support_waiting`：`status='waiting'|'notified'`）をポーリングし、
 *   行が **`notified`（＝空きが出た）へ変化した瞬間**にだけ通知音を鳴らす。
 * - 初回取得時は既存の通知済みを「見た扱い」にして、起動時に鳴らさない。
 * - 画面が非表示のときは共有ポーリング基盤（`usePolling`）が全タスクをスキップするため鳴らない
 *   （閉じている間は既存の Web Push＝OS通知が担当する）。
 * - 通知設定（`itSupport`）を OFF にしている利用者には鳴らさない。
 * - OSのアプリバッジ（対応端末のみ）は、未読の ITサポート空き通知の件数に同期する（読み切ると消える）。
 *
 * アプリ全体で1度だけ使う想定（`ItAvailabilityAlert` を `App` にマウント）。
 */
export function useItAvailabilityAlert() {
  const { user } = useAuth();
  const { isEnabled, itSupportSound } = useNotificationSettings(user?.id);

  const initializedRef = useRef(false);
  const seenNotifiedRef = useRef<Set<string>>(new Set());
  const enabledRef = useRef(isEnabled);
  enabledRef.current = isEnabled;
  const soundRef = useRef(itSupportSound);
  soundRef.current = itSupportSound;

  /** 未読の ITサポート空き通知の件数を OS のアプリバッジへ同期する（0 なら消す）。 */
  const syncAppBadge = useCallback(async (userId: string) => {
    const nav = navigator as BadgeNavigator;
    if (!nav.setAppBadge || !nav.clearAppBadge) return;
    try {
      const { count } = await supabase
        .from('task_notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('type', AVAILABILITY_TYPE)
        .eq('is_read', false);
      if (count && count > 0) await nav.setAppBadge(count);
      else await nav.clearAppBadge();
    } catch {
      // バッジ同期の失敗は無視（通知自体はベルで担保される）。
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const userId = user.id;

    // ログインユーザーが切り替わったら基準を取り直す。
    initializedRef.current = false;
    seenNotifiedRef.current = new Set();

    let active = true;

    const checkWaiting = async () => {
      try {
        const { data, error } = await supabase
          .from('it_support_waiting')
          .select('id, status')
          .eq('requester_id', userId)
          .in('status', ['waiting', 'notified']);
        if (error) throw error;
        if (!active) return;

        const notifiedIds = (data ?? [])
          .filter((r) => (r as { status: string }).status === 'notified')
          .map((r) => (r as { id: string }).id);

        if (!initializedRef.current) {
          // 初回は既存の通知済みを基準に取り込み、起動時に鳴らさない。
          seenNotifiedRef.current = new Set(notifiedIds);
          initializedRef.current = true;
          return;
        }

        const fresh = notifiedIds.filter((id) => !seenNotifiedRef.current.has(id));
        if (fresh.length === 0) return;
        fresh.forEach((id) => seenNotifiedRef.current.add(id));

        // 通知設定（ITサポート）が OFF、または「空き通知の音」が OFF のときは鳴らさない。
        if (enabledRef.current('itSupport') && soundRef.current) {
          playNotificationChime();
        }
      } catch {
        // 自動取得の失敗は無視（直前の状態を保つ）。
      }
    };

    void checkWaiting();
    const unregisterWaiting = registerPolling(() => {
      void checkWaiting();
    }, POLL_FALLBACK);
    const unregisterBadge = registerPolling(() => {
      void syncAppBadge(userId);
    }, POLL_FALLBACK_SLOW);

    // ページが表示に戻ったタイミングでも即座に確認する（ポーリング待ちを避ける）。
    const onVisible = () => {
      if (document.visibilityState === 'visible') void checkWaiting();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      active = false;
      unregisterWaiting();
      unregisterBadge();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, syncAppBadge]);
}